import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, Product, ProductCategory } from '../api';
import Footer from '../Components/Footer';
import ProductCard from '../Components/ProductCard';
import { Alert, Loading, useLoad } from '../Components/ui';

type GroupKey = ProductCategory | 'transfer';
const groupOf = (p: Product): GroupKey => (p.isTransfer ? 'transfer' : p.category);

const groups: Array<{ category: GroupKey; label: string; title: string; text: string }> = [
    {
        category: 'wordpress', label: 'WordPress', title: 'WordPress Hosting',
        text: 'Managed WordPress on CarbonEdge — installed for you, kept up to date and backed up.',
    },
    {
        category: 'static', label: 'Static', title: 'Static Web Hosting',
        text: 'For HTML sites, SPAs and static site generators. Upload over SFTP or edit in the file manager.',
    },
    {
        category: 'domain', label: 'Domains', title: 'Domain Registration',
        text: 'Register a new domain. DNS hosting on our nameservers is included.',
    },
    {
        category: 'transfer', label: 'Transfer', title: 'Domain Transfer',
        text: 'Already have a domain with another registrar? Move it to Forget-IT with its auth code (EPP code). Free for any domain ending we support, with the renewal included.',
    },
    {
        category: 'dns', label: 'DNS', title: 'DNS Hosting',
        text: 'Already have a domain? Move its DNS to CarbonEdge and manage it from our panel.',
    },
];

export default function Hosting() {
    const navigate = useNavigate();
    const location = useLocation();
    const { data: products, error, loading } = useLoad(
        () => api.get<{ products: Product[] }>('/products').then(r => r.data.products), []);

    const byCategory = useMemo(() => {
        const map = new Map<GroupKey, Product[]>();
        for (const p of products ?? []) map.set(groupOf(p), [...(map.get(groupOf(p)) ?? []), p]);
        return map;
    }, [products]);

    // Support deep links like /hosting#wordpress from the home page promo.
    useEffect(() => {
        if (!products || !location.hash) return;
        document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [products, location.hash]);

    const scrollTo = (category: string) => {
        document.getElementById(category)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    // Checkout requires an account; RequireAuth sends visitors to sign-up and back here afterwards.
    const select = (product: Product) => navigate(`/checkout/${product.slug}`);

    return (
        <>
            <section className="section page-section hosting-hero">
                <span className="section-label">Hosting</span>
                <h1 className="page-title">Hosting by Forget-IT</h1>
                <p className="section-desc">
                    Choose a plan below. Everything runs on our CarbonEdge infrastructure, set up automatically the moment your payment is confirmed.
                </p>

                <div className="category-tabs">
                    {groups.filter(g => byCategory.has(g.category)).map(g => (
                        <button key={g.category} className="pill" onClick={() => scrollTo(g.category)}>{g.label}</button>
                    ))}
                </div>

                {loading && <Loading text="Loading plans…" />}
                {error && <Alert type="error">{error}</Alert>}

                {groups.filter(g => byCategory.has(g.category)).map(group => {
                    const items = byCategory.get(group.category)!;
                    return (
                        <div className="catalog-group" id={group.category} key={group.category}>
                            <h2>{group.title}</h2>
                            <p className="section-desc">{group.text}</p>
                            <div className={`card-grid cols-${Math.min(items.length, 3)}`}>
                                {items.map((product, i) => (
                                    <ProductCard key={product.slug} product={product} onSelect={select} delay={i * 100} />
                                ))}
                            </div>
                        </div>
                    );
                })}
            </section>
            <Footer />
        </>
    );
}
